/*
 *  This file location-dropdowns.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2023  
 *  (C) 2015-2023. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *  
 */

import { Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { subBranchGridRequestData } from '../../../modules/core/organization/location/sub-branch/sub-branch.model';
import { HroneGlobalStateService } from '../../../store/global/global-state.service';
import { CityModel, StateModel } from '../../../modules/onboarding-config/state-management/onboarding.models';
import { TKeyValue } from '../../models/common.models';
import { HttpService } from '../http-service';
import { globalRegionUrls, GlobalRegionGridResponseModel } from '../../../modules/core/organization/location/global-region/global-region.model';
import { businessRegionUrls, BusinessRegionGridResponseModel } from '../../../modules/core/organization/location/business-region/business-region.model';
import { districtUrls, DistrictGridResponseModel } from '../../../modules/core/organization/location/district/district.model';

enum locatonDdUrls {
  branchMapped = '/api/core/Branch/Mapped/',
  stateCountryWise = '/api/core/State/BindDropDown',
  cityStateWise = '/api/core/City/BindDropDown',
  subBranchBranchWise = '/api/core/SubBranch/BindDropDown',
  branchStateWise = '/api/core/Branch/BindDropDown'
}

export type TStateDropdownItem = TKeyValue<string, string> & {
  countryCode: string;
  businessRegionCode?: string;
  businessRegionName?: string;
};

export type TCityDropdownItem = TKeyValue<string, string> & {
  districtCode?: string;
  districtName?: string;
};

@Injectable({
  providedIn: 'root'
})
export class LocationDropdownsService {

  countryDd: any[] = [];
  countryPromise: Promise<any> | null = null;
  stateDd: any[] = [];
  cityDd: any[] = [];
  stateDdCountryWise: any[] = [];
  cityDdStateWise: any[] = [];
  regionDd: any[] = [];
  branchDd: any[] = [];
  subBranchDd: any[] = [];
  subBranchDdBranchWise: any[] = [];
  globalRegionDd: any[] = [];
  businessRegionDd: any[] = [];
  districtDd: any[] = [];

  countryActiveDd: Array<TKeyValue<string,string> & {isdCode:string,globalRegionId:string | number,globalRegionName:string}> = [];
  stateActiveDd: any[] = [];
  cityActiveDd: any[] = [];
  stateActiveDdCountryWise: any[] = [];
  cityActiveDdStateWise: any[] = [];
  lastCityStateCode = '';
  regionActiveDd:(TKeyValue<string> &{cityCode:string;cityName:string})[] = [];
  branchActiveDd:TKeyValue<string>[] = [];
  subBranchActiveDd: TKeyValue<string>[] = [];
  subBranchActiveDdBranchWise: any[] = [];
  globalRegionActiveDd: any[] = [];
  businessRegionActiveDd: any[] = [];
  districtActiveDd: any[] = [];

  branchMappedActiveDd: any[] = [];
  branchActiveDdStateWise: any[] = [];
  branchDdStateWiseForChallan: any[] = []
  branchActiveDdWithState: any[] = []

  constructor(
    private readonly _httpService: HttpService,
    private readonly _globalStateService: HroneGlobalStateService
  ) { }

  resetCountryDropDown() {
    this.countryDd = [];
    this.countryPromise = null;
    this.getCountryDropDown();
  }

  getCountryDropDown(bgvVerificationDetails: any[] = []) {
    if (!this.countryPromise) {
      this.countryPromise = new Promise((resolve, reject) => {
        if (this.countryDd.length) {
          resolve(this.countryActiveDd);
          return;
        }
        this._globalStateService.getCountries().subscribe((response) => {
          const list = sortBy(response as TCoreCountryResponseItem[], [e => e.countryName.toLowerCase()]);
          this.countryDd = list.map(mapper => ({ value: mapper.countryName, key: mapper.countryCode, isdCode: mapper.internationalCode, globalRegionId: mapper.globalRegionCode || mapper.globalRegionId, globalRegionName: mapper.globalRegionName }));

          const filteredList = list.filter(data => data.activeStatus === '1');
          this.countryActiveDd = filteredList.map(mapper => ({ value: mapper.countryName, key: mapper.countryCode, isdCode: mapper.internationalCode, globalRegionId: (mapper.globalRegionCode || mapper.globalRegionId) as string | number, globalRegionName: mapper.globalRegionName }));
          bgvVerificationDetails.forEach(bgvDetail => {
            if ((bgvDetail.originalFieldName === 'FK_COUNTRYCODE_CUR' || bgvDetail.originalFieldName === 'FK_COUNTRYCODE_PER') && bgvDetail.aadhaarKycFieldValue) {
              const country = this.countryActiveDd.find(country => (country.value.toLowerCase() == bgvDetail.aadhaarKycFieldValue.toLowerCase()) || (country.value.toLowerCase() == bgvDetail.aadharValue.toLowerCase()));
              bgvDetail.aadhaarKycFieldValue = country ? country.key.toString() : bgvDetail.aadhaarKycFieldValue;
            }
            if ((bgvDetail.originalFieldName === 'FK_COUNTRYCODE_CUR' || bgvDetail.originalFieldName === 'FK_COUNTRYCODE_PER') && bgvDetail.panKycFieldValue) {
              const country = this.countryActiveDd.find(country => (country.value.toLowerCase() == bgvDetail.panKycFieldValue.toLowerCase()) || (country.value.toLowerCase() == bgvDetail.panValue.toLowerCase()));
              bgvDetail.panKycFieldValue = country ? country.key.toString() : bgvDetail.panKycFieldValue;
            }
          })
          resolve(this.countryActiveDd)
        });
      });
    }
    return this.countryPromise as Promise<any>
  }

  resetStateDropDown() {
    this.stateDd = [];
    this.getStateDropDown();
  }

  getStateDropDown() {

    if (this.stateDd.length) {
      return;
    }

    this._globalStateService.getStates().subscribe((response) => {
      const list = sortBy(response, [e => e.stateName.toLowerCase()]);
      this.stateDd = list.map(mapper => ({ value: mapper.stateName, key: mapper.stateCode, businessRegionCode: mapper.businessRegionCode, businessRegionName: mapper.businessRegionName }));

      const filteredList = list.filter(data => data.activeStatus === '1');
      this.stateActiveDd = filteredList.map(mapper => ({ value: mapper.stateName, key: mapper.stateCode, businessRegionCode: mapper.businessRegionCode, businessRegionName: mapper.businessRegionName }));

    });
  }

  getStateDropDownCountryWise(countryCode: string | number | undefined, bgvVerificationDetails: any[] = []) {
    this.stateDdCountryWise = [];
    this.stateActiveDdCountryWise = [];
    if (countryCode) {
      this._globalStateService.getStates(countryCode.toString()).subscribe((response) => {
        const list = sortBy(response, [e => e.stateName.toLowerCase()]);
        this.stateDdCountryWise = list.map(mapper => ({ value: mapper.stateName, key: mapper.stateCode, countryCode: mapper.countryCode, businessRegionCode: mapper.businessRegionCode, businessRegionName: mapper.businessRegionName }));

        const filteredList = list.filter(data => data.activeStatus === '1');
        this.stateActiveDdCountryWise = filteredList.map(mapper => ({ value: mapper.stateName, key: mapper.stateCode, countryCode: mapper.countryCode, businessRegionCode: mapper.businessRegionCode, businessRegionName: mapper.businessRegionName }));
        bgvVerificationDetails.forEach(bgvDetail => {
          if (bgvDetail.originalFieldName === 'FK_STATECODE_CUR' && bgvDetail.aadhaarKycFieldValue) {
            const state = this.stateActiveDdCountryWise.find(state => (state.value.toLowerCase() == bgvDetail.aadhaarKycFieldValue.toLowerCase()) || (state.value.toLowerCase() == bgvDetail.aadharValue.toLowerCase()));
            bgvDetail.aadhaarKycFieldValue = state ? state.key.toString() : bgvDetail.aadhaarKycFieldValue;
          }
          if (bgvDetail.originalFieldName === 'FK_STATECODE_CUR' && bgvDetail.panKycFieldValue) {
            const state = this.stateActiveDdCountryWise.find(state => (state.value.toLowerCase() == bgvDetail.panKycFieldValue.toLowerCase()) || (state.value.toLowerCase() == bgvDetail.panValue.toLowerCase()));
            bgvDetail.panKycFieldValue = state ? state.key.toString() : bgvDetail.panKycFieldValue;
          }
        })
      });
    }
  }

  resetCityDropDown() {
    this.cityDd = [];
    this.getCityDropDown();
  }

  getCityDropDown(isOtherRequired = false) {

    if (this.cityDd.length) {
      const index = this.cityActiveDd.findIndex(city => city.key == 0);
      if (isOtherRequired && index == -1)
        this.cityActiveDd.push({ value: 'Other', key: '0' });
      else if (!isOtherRequired && index > -1)
        this.cityActiveDd.splice(index, 1);
      return;
    }

    this._globalStateService.getCities().subscribe((response) => {
      const list = sortBy(response, [e => e.cityName.toLowerCase()]);
      this.cityDd = list.map(mapper => ({ value: mapper.cityName, key: mapper.cityCode ,districtCode: mapper.districtCode, districtName: mapper.districtName}));

      const filteredList = list.filter(data => data.activeStatus === '1');
      this.cityActiveDd = filteredList.map(mapper => ({ value: mapper.cityName, key: mapper.cityCode ,districtCode: mapper.districtCode, districtName: mapper.districtName}));
      if (isOtherRequired)
        this.cityActiveDd.push({ value: 'Other', key: '0' });
    });
  }

  getCityDropDownStateWise(stateCode: string | number | undefined, bgvVerificationDetails: any[] = []) {
    this.lastCityStateCode = stateCode?.toString() ?? '';
    this.cityDdStateWise = [];
    this.cityActiveDdStateWise = [];
    if (stateCode) {
      this._globalStateService.getCities(stateCode.toString()).subscribe(
        (response) => {

          const list = sortBy(response, [e => e.cityName.toLowerCase()]);
        this.cityDdStateWise = list.map(mapper => ({ value: mapper.cityName, key: mapper.cityCode ,districtCode: mapper.districtCode, districtName: mapper.districtName}));

          const filteredList = list.filter(data => data.activeStatus === '1');
          this.cityActiveDdStateWise = filteredList.map(mapper => ({ value: mapper.cityName, key: mapper.cityCode ,districtCode: mapper.districtCode, districtName: mapper.districtName}));
          bgvVerificationDetails.forEach(bgvDetail => {
            if (bgvDetail.originalFieldName === 'FK_CITY_CODE_CUR' && bgvDetail.aadhaarKycFieldValue) {
              const city = this.cityActiveDdStateWise.find(city => (city.value.toLowerCase() == bgvDetail.aadhaarKycFieldValue.toLowerCase()) || (city.value.toLowerCase() == bgvDetail.aadharValue.toLowerCase()));
              bgvDetail.aadhaarKycFieldValue = city ? city.key.toString() : bgvDetail.aadhaarKycFieldValue;
            }
            if (bgvDetail.originalFieldName === 'FK_CITY_CODE_CUR' && bgvDetail.panKycFieldValue) {
              const city = this.cityActiveDdStateWise.find(city => (city.value.toLowerCase() == bgvDetail.panKycFieldValue.toLowerCase()) || (city.value.toLowerCase() == bgvDetail.panValue.toLowerCase()));
              bgvDetail.panKycFieldValue = city ? city.key.toString() : bgvDetail.panKycFieldValue;
            }
          })
        });
    }
  }

  getStatesObservable(countryCode: string | number): Observable<TStateDropdownItem[]> {
    if (!countryCode) return of([]);
    return this._globalStateService.getStates(countryCode.toString()).pipe(
      map((response: StateModel[]) => {
        const list = sortBy(response, [(e: StateModel) => e.stateName.toLowerCase()]);
        return list
          .filter((data: StateModel) => data.activeStatus === '1')
          .map((mapper: StateModel): TStateDropdownItem => ({
            value: mapper.stateName,
            key: mapper.stateCode,
            countryCode: mapper.countryCode,
            businessRegionCode: mapper.businessRegionCode,
            businessRegionName: mapper.businessRegionName
          }));
      })
    );
  }

  getCitiesObservable(stateCode: string | number): Observable<TCityDropdownItem[]> {
    if (!stateCode) return of([]);
    return this._globalStateService.getCities(stateCode.toString()).pipe(
      map((response: CityModel[]) => {
        const list = sortBy(response, [(e: CityModel) => e.cityName.toLowerCase()]);
        return list
          .filter((data: CityModel) => data.activeStatus === '1')
          .map((mapper: CityModel): TCityDropdownItem => ({
            value: mapper.cityName,
            key: mapper.cityCode,
            districtCode: mapper.districtCode,
            districtName: mapper.districtName
          }));
      })
    );
  }

  resetGlobalRegionDropDown() {
    this.globalRegionDd = [];
    this.globalRegionActiveDd = [];
    this.getGlobalRegionDropDown();
  }

  getGlobalRegionDropDown() {
    this._httpService.Get(globalRegionUrls.globalRegionList).subscribe((response: GlobalRegionGridResponseModel[]) => {
      const list = sortBy(response ?? [], [e => (e.globalRegionName ?? '').toLowerCase()]);
      this.globalRegionDd = list.map(mapper => ({ value: mapper.globalRegionName, key: mapper.globalRegionCode }));
      const filteredList = list.filter(data => data.activeStatus === '1');
      this.globalRegionActiveDd = filteredList.map(mapper => ({ value: mapper.globalRegionName, key: mapper.globalRegionCode }));
    });
  }

  resetBusinessRegionDropDown() {
    this.businessRegionDd = [];
    this.businessRegionActiveDd = [];
    this.getBusinessRegionDropDown();
  }

  getBusinessRegionDropDown() {
    if (this.businessRegionDd.length) {
      return;
    }
    this._httpService.Get(businessRegionUrls.businessRegionList).subscribe((response: BusinessRegionGridResponseModel[]) => {
      const list = sortBy(response || [], [e => (e.businessRegionName || '').toLowerCase()]);
      this.businessRegionDd = list.map(mapper => ({ value: mapper.businessRegionName, key: mapper.businessRegionCode , countryCode: mapper.countryCode }));
      const filteredList = list.filter(data => data.activeStatus === '1');
      this.businessRegionActiveDd = filteredList.map(mapper => ({ value: mapper.businessRegionName, key: mapper.businessRegionCode , countryCode: mapper.countryCode }));
    });
  }

  resetDistrictDropDown() {
    this.districtDd = [];
    this.districtActiveDd = [];
    this.getDistrictDropDown();
  }

  getDistrictDropDown() {
    if (this.districtDd.length) {
      return;
    }
    this._httpService.Get(districtUrls.districtList).subscribe((response: DistrictGridResponseModel[]) => {
      const list = sortBy(response ?? [], [e => (e.districtName ?? '').toLowerCase()]);
      this.districtDd = list.map(mapper => ({ value: mapper.districtName, key: mapper.districtCode ,stateCode: mapper.stateCode, stateName: mapper.stateName}));
      const filteredList = list.filter(data => data.activeStatus === '1');
      this.districtActiveDd = filteredList.map(mapper => ({ value: mapper.districtName, key: mapper.districtCode ,stateCode: mapper.stateCode, stateName: mapper.stateName}));
    });
  }

  resetRegionDropDown() {
    this.regionDd = [];
    this.getRegionDropDown();
  }

  getRegionDropDown(): Promise<TKeyValue<string>[]> {
    return new Promise((resolve, reject) => {
      if (this.regionDd.length) {
        resolve([...this.regionActiveDd]);
        return;
      }

      this._globalStateService.getRegions().subscribe((response) => {
        const list = sortBy(response, [e => e.regionName.toLowerCase()]);
        this.regionDd = list.map(mapper => ({ value: mapper.regionName, key: mapper.regionCode, cityCode: mapper.cityCode, cityName: mapper.cityName}));
        const filteredList = list.filter(data => data.activeStatus === '1');
        this.regionActiveDd = filteredList.map(mapper => ({ value: mapper.regionName, key: mapper.regionCode, cityCode: mapper.cityCode ?? '', cityName: mapper.cityName ?? ''}));
        resolve([...this.regionActiveDd]);
      });
    });
  }

  resetBranchDropDown() {
    this.branchDd = [];
    this.getBranchDropDown();
  }

  getBranchDropDown(): Promise<TKeyValue<string>[]> {
    return new Promise((resolve, reject) => {

      if (this.branchDd.length) {
        resolve([...this.branchActiveDd]);
        return;
      }

      this._globalStateService.getBranches().subscribe((response) => {
        const list = sortBy(response, [e => e.branchName.toLowerCase()]);
        this.branchDd = list.map(mapper => ({ value: mapper.branchName, key: mapper.branchCode }));

        const filteredList = list.filter(data => data.activeStatus === '1');
        this.branchActiveDd = filteredList.map(mapper => ({ value: mapper.branchName, key: mapper.branchCode }));
        this.branchActiveDdWithState = filteredList.map(mapper => ({ value: mapper.branchName, key: mapper.branchCode, state: mapper.state }));
        resolve([...this.branchActiveDd]);
      });
    });
  }

  resetSubBranchDropDown() {
    this.subBranchDd = [];
    this.getSubBranchDropDown();
  }

  getSubBranchDropDown(): Promise<TKeyValue<string>[]> {
    return new Promise((resolve, reject) => {
      if (this.subBranchDd.length) {
        resolve([...this.subBranchActiveDd]);
        return;
      }

      this._globalStateService.getSubBranches().subscribe((response) => {
        const list = sortBy(response, [e => e.subBranchName.toLowerCase()]);
        this.subBranchDd = list.map(mapper => ({ value: mapper.subBranchName, key: mapper.subBranchCode }));

        const filteredList = list.filter(data => data.activeStatus === '1');
        this.subBranchActiveDd = filteredList.map(mapper => ({ value: mapper.subBranchName, key: mapper.subBranchCode }));

        resolve([...this.subBranchActiveDd]);
      });
    });
  }

  getSubBranchDropDownBranchWise(branchCode: string | number | undefined) {
    this.subBranchDdBranchWise = [];
    this.subBranchActiveDdBranchWise = [];
    const subBrancGridRequest = subBranchGridRequestData;
    subBrancGridRequest.branchCodeFilter = branchCode?.toString() ?? '';
    subBrancGridRequest.pagination.pageNumber = 0;
    subBrancGridRequest.pagination.pageSize = 0;
    subBrancGridRequest.subBranchStatusFilter = '0,1';

    return new Promise((resolve) => {
        this._httpService.Post(locatonDdUrls.subBranchBranchWise, subBrancGridRequest).subscribe((res) => {
            const response = res.subBranchModel ? res.subBranchModel : [];
            const list = sortBy(response, [e => e.subBranchName.toLowerCase()]);
            this.subBranchDdBranchWise = list.map(mapper => ({ 
                value: mapper.subBranchName, 
                key: mapper.subBranchCode 
            }));

            const filteredList = list.filter(data => data.activeStatus === '1');
            this.subBranchActiveDdBranchWise = filteredList.map(mapper => ({ 
                value: mapper.subBranchName, 
                key: mapper.subBranchCode 
            }));
            resolve(this.subBranchActiveDdBranchWise);
        });
    });
  }

  getBranchMappedDropDown(buCode: string | number | undefined, regionCode: string | number | undefined) {
    this.branchMappedActiveDd = [];
    return new Promise((resolve) => {
        this._httpService.Get(locatonDdUrls.branchMapped + buCode + '/' + regionCode).subscribe((response) => {
            const list = sortBy(response, [e => e.branchName.toLowerCase()]);
            const filteredList = list.filter(data => data.activeStatus === '1');
            this.branchMappedActiveDd = filteredList.map(mapper => ({ 
                value: mapper.branchName, 
                key: mapper.branchCode 
            }));
            resolve(this.branchMappedActiveDd);
        });
    });
  }

  getBranchOnStateChangedDropDown(stateCode?: string,isZeroIncluded? : boolean):Observable<null|{key:string;value:string}[]> {
    if (!stateCode) {
      return of(null);
    }
    this.branchActiveDdStateWise = [];
    const branchGridRequest: any = {
      branchCodeFilter: '',
      branchStatusFilter: isZeroIncluded ? '1,0' : '1',
      branchRegionFilter: '',
      branchStateFilter: stateCode,
      pagination: {
        pageNumber: 0,
        pageSize: 0
      }
    };
    return this._httpService.Post(locatonDdUrls.branchStateWise, branchGridRequest)
      .pipe(map(
        (response) => {
          const list = sortBy(response, [e => e.branchName.toLowerCase()]);
          const filteredList = list.filter(data => data.activeStatus === '1');
          this.branchActiveDdStateWise = isZeroIncluded ? response.map((mapper:{branchName:string;branchCode:string}) => ({value: mapper.branchName, key: mapper.branchCode}))  : filteredList.map(mapper => ({ value: mapper.branchName, key: mapper.branchCode }));
          return this.branchActiveDdStateWise;
        }));
  }

  reset() {
    this.countryDd = [];
    this.stateDd = [];
    this.cityDd = [];
    this.stateDdCountryWise = [];
    this.cityDdStateWise = [];
    this.regionDd = [];
    this.branchDd = [];
    this.subBranchDd = [];
    this.subBranchDdBranchWise = [];
    this.countryActiveDd = [];
    this.stateActiveDd = [];
    this.cityActiveDd = [];
    this.stateActiveDdCountryWise = [];
    this.cityActiveDdStateWise = [];
    this.regionActiveDd = [];
    this.branchActiveDd = [];
    this.subBranchActiveDd = [];
    this.subBranchActiveDdBranchWise = [];
    this.branchMappedActiveDd = [];
    this.branchActiveDdStateWise = [];
    this.branchDdStateWiseForChallan = [];
    this.globalRegionDd = [];
    this.businessRegionDd = [];
    this.districtDd = [];
    this.globalRegionActiveDd = [];
    this.businessRegionActiveDd = [];
    this.districtActiveDd = [];
    this.countryPromise = null;
  }

  allDropDown() {
    this.getRegionDropDown();
    this.getBranchDropDown();
    this.getSubBranchDropDown();
  }

}

export type TCoreCountryResponseItem = {
  logOnUserCode:        string;
  domainCode:           string;
  logOnUserId:          number;
  logOnUserName:        string;
  currentUserLogOnType: string;
  countryCode:          string;
  countryName:          string;
  internationalCode:    string;
  nationality:          string;
  activeStatus:         string;
  globalRegionCode?:       number;
  globalRegionId?:       number;
  globalRegionName:     string;
}